// Names for the ids a task group carries: its owner, task type and pinned workflow version. The
// group rows hold ids only, so the admin pages join them here from the lists the viewer may read
// (users with ViewUsers, task types with ManageTaskTypes or BrowseMarketplace, workflows and
// their versions with ViewWorkflows); without a permission the id stands in for the name.
import { queryOptions, useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import { unwrap } from "../api/queries";
import { requestJson } from "../api/http";
import { taskTypesQuery } from "../catalog/api";
import { parseWorkflows } from "../catalog/parse";
import { usersQuery } from "../auth/admin";
import { usePermissions } from "../auth/permissions";
import { shortId } from "../pages/format";

export interface PinnedVersion {
  workflowId: string;
  workflowName: string;
  version: number;
}

export interface Lookups {
  owner: (userId: string) => string;
  taskType: (id: string) => string;
  version: (versionId: string) => PinnedVersion | undefined;
  /** Every owner and task type known, for the filters. */
  owners: { id: string; name: string }[];
  taskTypes: { id: string; name: string }[];
}

export const marketplaceTypesQuery = () =>
  queryOptions({ queryKey: ["marketplace", "task-types"], queryFn: () => unwrap(api.GET("/api/task-types")) });

/** Version id → workflow and number, across every workflow's versions. */
export const versionIndexQuery = () =>
  queryOptions({
    queryKey: ["catalog", "version-index"],
    queryFn: async () => {
      const workflows = parseWorkflows(await requestJson("GET", "/api/admin/workflows"));
      const lists = await Promise.all(
        workflows.map(async (w) => ({
          w,
          versions: await unwrap(api.GET("/api/admin/workflows/{workflow_id}/versions", { params: { path: { workflow_id: w.id } } })),
        })),
      );
      const index = new Map<string, PinnedVersion>();
      for (const { w, versions } of lists) {
        for (const v of versions) index.set(v.id, { workflowId: w.id, workflowName: w.name, version: v.version });
      }
      return index;
    },
  });

export function useLookups(): Lookups {
  const { can } = usePermissions();
  const users = useQuery({ ...usersQuery(), enabled: can("ViewUsers") });
  const adminTypes = useQuery({ ...taskTypesQuery(), enabled: can("ManageTaskTypes") });
  const listedTypes = useQuery({ ...marketplaceTypesQuery(), enabled: !can("ManageTaskTypes") && can("BrowseMarketplace") });
  const versions = useQuery({ ...versionIndexQuery(), enabled: can("ViewWorkflows") });

  const userNames = new Map((users.data ?? []).map((u) => [u.id, u.display_name || u.email]));
  const types = adminTypes.data ?? listedTypes.data ?? [];
  const typeNames = new Map(types.map((t) => [t.id, t.name]));
  return {
    owner: (id) => userNames.get(id) ?? shortId(id),
    taskType: (id) => typeNames.get(id) ?? shortId(id),
    version: (id) => versions.data?.get(id),
    owners: (users.data ?? []).map((u) => ({ id: u.id, name: u.display_name || u.email })),
    taskTypes: types.map((t) => ({ id: t.id, name: t.name })),
  };
}
